import { Icon } from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { BreakingLayout, HLayout } from '@sleepstation/components-layouts'
import { DIRECTION, SIZE } from '@sleepstation/const'
import {
  backgroundColor,
  baseline,
  borderLeftRadius,
  borderTopRadius,
  color,
  left,
  mq,
  padding,
  right,
  stylesIfPropFalse,
  themeFontBody,
  top,
} from '@sleepstation/style-api-helpers'
import { mqPointerOnly, outset } from '@sleepstation/style-mixins'
import { defaultTheme, reversedTheme } from '@sleepstation/themes-public-site'
import { rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import CardImage from '../CardImage'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const LABEL_NEW = `New`

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Wrapper = styled(LinkBase)`
  display: block;
  height: 100%;
  text-decoration-line: none;

  ${mq(`mediumOnly`)} {
    max-width: ${rem(452)};
  }

  ${mq(`largeUp`)} {
    max-width: ${rem(592)};
  }
`

const Layout = styled(BreakingLayout).attrs({
  direction: [DIRECTION.HORIZONTAL, DIRECTION.VERTICAL],
})`
  ${outset()}
  ${borderTopRadius(0.5)}
  overflow: hidden;
  height: 100%;
  position: relative;

  ${mq(`xSmallOnly`)} {
    height: ${rem(125)};
  }

  ${mqPointerOnly()} {
    &:hover .illustration {
      will-change: transform;
      transform: scale3d(1.025, 1.025, 1);
    }
  }
`

const Body = styled.div``

const IconWrapper = styled(HLayout)`
  position: absolute;
  ${top(0.25)}
  ${left(0.075)}

  ${color(`body`)}

  ${mq(`smallUp`)} {
    ${top(0.5)}
    ${right(0.35)}
    left: auto;
  }

  /* stylelint-disable-next-line */
  > *:last-child {
    position: relative;
    z-index: 1;
  }
`

const NewLabel = styled.div`
  ${padding([[0, 0.5, 0, 0.25]])}
  ${color(`body`)}
  ${baseline([0])}
  ${themeFontBody(`demi`)}
  ${backgroundColor(`primaryBackgroundStart`)}
  ${borderLeftRadius(0.25)}
  ${right(-0.25)}
  position: relative;
  z-index: 0;
  ${stylesIfPropFalse(`isNew`, {
    visibility: `hidden`,
  })}
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Card = ({ image, url, iconName, children, dataCy, isNew }) => (
  <Wrapper href={url} data-cy={dataCy}>
    <Layout>
      <CardImage image={image} />
      <Body>{children}</Body>
      {iconName && (
        <ThemeProvider theme={reversedTheme}>
          <IconWrapper>
            <ThemeProvider theme={defaultTheme}>
              <NewLabel isNew={isNew}>{LABEL_NEW}</NewLabel>
            </ThemeProvider>
            <Icon size={SIZE.LARGE} name={iconName} />
          </IconWrapper>
        </ThemeProvider>
      )}
    </Layout>
  </Wrapper>
)

Card.propTypes = {
  image: PropTypes.object,
  url: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  iconName: PropTypes.string,
  dataCy: PropTypes.string,
  isNew: PropTypes.bool.isRequired,
}

Card.defaultProps = {
  image: undefined,
  iconName: undefined,
  dataCy: undefined,
}

export default Card
